// Floor-polygon corner classification for corner-trap mounts.
//
// Lives in core (not floorplan-core) so the editor reducer can reseat traps
// on SET_FOOTPRINT / hydrate without a core → floorplan-core import cycle.
// The room polygon is CCW; y on the floor plan is world Z.

import type { EditorDoc, PanelEntry } from './editorState';

export interface Poly2 { x: number; y: number }

const CONVEX_EPS = 1e-9;

/** Interior angle at vertex `i` (radians), in (0, 2π).
 *
 *  CCW polygon: a rectangle corner is π/2, an L-room inner notch is 3π/2. */
export function interiorAngleAt(poly: readonly Poly2[], i: number): number {
  const n = poly.length;
  if (n < 3) return Math.PI;
  const a = poly[(i - 1 + n) % n];
  const b = poly[i];
  const c = poly[(i + 1) % n];
  const e1x = b.x - a.x;
  const e1y = b.y - a.y;
  const e2x = c.x - b.x;
  const e2y = c.y - b.y;
  if (e1x * e1x + e1y * e1y < CONVEX_EPS || e2x * e2x + e2y * e2y < CONVEX_EPS) {
    return Math.PI;
  }
  const cross = e1x * e2y - e1y * e2x;
  const dot = e1x * e2x + e1y * e2y;
  const turn = Math.atan2(cross, dot);
  return Math.PI - turn;
}

/** True when the interior at `i` is strictly less than π (a bass-trap corner). */
export function isConvexCorner(poly: readonly Poly2[], i: number): boolean {
  return interiorAngleAt(poly, i) < Math.PI - CONVEX_EPS;
}

/** Floor polygon for trap math: explicit footprint, else the axis-aligned box. */
export function roomFootprintPolygon(doc: Pick<EditorDoc, 'dims' | 'footprint'>): Poly2[] {
  if (doc.footprint && doc.footprint.length >= 3) {
    return doc.footprint.map((c) => ({ x: c.x, y: c.y }));
  }
  const { Lx, Ly } = doc.dims;
  return [
    { x: 0, y: 0 }, { x: Lx, y: 0 }, { x: Lx, y: Ly }, { x: 0, y: Ly },
  ];
}

/** Inward unit bisector in the floor plane (x, y=world-z). Matches roomSurfaces. */
export function inwardBisectorAt(poly: readonly Poly2[], i: number): Poly2 | null {
  const n = poly.length;
  if (n < 3) return null;
  const prev = poly[(i - 1 + n) % n];
  const cur = poly[i];
  const next = poly[(i + 1) % n];
  const nA = inwardNormal2(prev, cur);
  const nB = inwardNormal2(cur, next);
  if (!nA || !nB) return null;
  const x = nA.x + nB.x;
  const y = nA.y + nB.y;
  const len = Math.hypot(x, y);
  if (len < 1e-9) return null;
  return { x: x / len, y: y / len };
}

/** Yaw that seats a trap's local +Z on the inward bisector — same as poseToTransform. */
export function cornerYawAt(poly: readonly Poly2[], i: number): number {
  const bis = inwardBisectorAt(poly, i);
  if (!bis) return 0;
  return Math.atan2(bis.x, bis.y);
}

export function nearestConvexCornerIndex(
  poly: readonly Poly2[], x: number, z: number,
): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < poly.length; i++) {
    if (!isConvexCorner(poly, i)) continue;
    const d = Math.hypot(poly[i].x - x, poly[i].y - z);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

/** Interior angle of the convex corner this trap is (or should be) seated on. */
export function interiorAngleForPanel(
  doc: Pick<EditorDoc, 'dims' | 'footprint'>,
  panel: Pick<PanelEntry, 'shape' | 'position'>,
): number {
  if (panel.shape !== 'corner') return Math.PI / 2;
  const poly = roomFootprintPolygon(doc);
  const i = nearestConvexCornerIndex(poly, panel.position[0], panel.position[2]);
  if (i < 0) return Math.PI / 2;
  return interiorAngleAt(poly, i);
}

/**
 * Snap every glued corner trap onto the nearest convex vertex and align its
 * yaw with that corner's inward bisector. Reflex vertices are not mounts.
 */
export function reseatCornerTraps(doc: EditorDoc): EditorDoc {
  const poly = roomFootprintPolygon(doc);
  if (poly.length < 3) return doc;
  let changed = false;
  const panels = doc.panels.map((p) => {
    if (p.shape !== 'corner' || p.detached) return p;
    const i = nearestConvexCornerIndex(poly, p.position[0], p.position[2]);
    if (i < 0) return p;
    const v = poly[i];
    const yaw = cornerYawAt(poly, i);
    const samePos =
      Math.abs(p.position[0] - v.x) < 1e-9
      && Math.abs(p.position[2] - v.y) < 1e-9;
    const sameYaw = Math.abs(p.rotation[1] - yaw) < 1e-9;
    if (samePos && sameYaw) return p;
    changed = true;
    return {
      ...p,
      position: [v.x, p.position[1], v.y] as PanelEntry['position'],
      rotation: [0, yaw, 0] as PanelEntry['rotation'],
    };
  });
  return changed ? { ...doc, panels } : doc;
}

/** CCW edge A→B: interior is to the left, so inward = rotate 90° CCW. */
function inwardNormal2(a: Poly2, b: Poly2): Poly2 | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return null;
  return { x: -dy / len, y: dx / len };
}
